import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';

/**
 * LocaleService: keeps Angular locale in sync with ngx-translate language.
 * It dynamically imports locale data from Angular's locales bundle when needed.
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private current = 'es';

  constructor(private translate: TranslateService) {
    // initialize from translate current language
    const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'es';
    this.setLocale(lang);

    // listen to future changes
    this.translate.onLangChange.subscribe(evt => {
      this.setLocale(evt.lang);
    });
  }

  getLocale(): string {
    return this.current;
  }

  async setLocale(lang: string) {
    if (!lang) { return; }
    this.current = lang;

    // Angular locale ids are like 'en', 'es', 'it', 'de' etc. For regional codes like 'pt-BR'
    // we map to the base language part so registerLocaleData can load it.
    const base = lang.split('-')[0];
    try {
      let module: any;
      // Import explicit known locales to avoid deep export issues in @angular/common
      switch (base) {
        case 'es':
          module = await import('@angular/common/locales/es');
          break;
        case 'en':
          module = await import('@angular/common/locales/en');
          break;
        case 'pt':
          module = await import('@angular/common/locales/pt');
          break;
        case 'fr':
          module = await import('@angular/common/locales/fr');
          break;
        case 'it':
          module = await import('@angular/common/locales/it');
          break;
        case 'de':
          module = await import('@angular/common/locales/de');
          break;
        case 'ca':
          module = await import('@angular/common/locales/ca');
          break;
        default:
          module = await import('@angular/common/locales/en');
          break;
      }

      registerLocaleData(module.default || module);
    } catch (e) {
      // best-effort fallback to English
      try {
        const def = await import('@angular/common/locales/en');
        registerLocaleData(def.default || def);
      } catch (_e) {
        // give up silently
      }
    }
  }
}
